"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import Script from "next/script";
import "./styles.scss";

export const TRUSTPILOT_SCRIPT_SRC =
  "https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js";
export const TRUSTPILOT_DEFAULT_LOCALE = "en-US";

const TRUSTPILOT_BUSINESS_UNIT_ID = "64f888eb7b782062676ccb0d";
const TRUSTPILOT_REVIEW_URL = "https://www.trustpilot.com/review/qrcreator.com";

type TrustpilotVariant = "mini" | "microStar";

interface TrustpilotProps {
  variant?: TrustpilotVariant;
  /**
   * Locale used to render the widget content (e.g. "es-ES").
   * Trustpilot falls back to English for unsupported locales.
   */
  locale?: string | null;
}

interface TrustBoxProps {
  trustBoxRef: React.RefObject<HTMLDivElement>;
  locale: string;
}

const TrustpilotMini = ({ trustBoxRef, locale }: TrustBoxProps) => {
  return (
    <div
      ref={trustBoxRef}
      className="trustpilot-widget trustBox-mini"
      data-locale={locale}
      data-template-id="53aa8807dec7e10d38f59f32"
      data-businessunit-id={TRUSTPILOT_BUSINESS_UNIT_ID}
      data-style-height="100px"
      data-style-width="100%"
      data-font-family="Nunito Sans"
      data-qa="trustpilot-widget"
      data-testid="trustpilot-widget"
    >
      <a
        href={TRUSTPILOT_REVIEW_URL}
        target="_blank"
        rel="noopener"
      ></a>
    </div>
  );
};

const TrustpilotMicroStar = ({ trustBoxRef, locale }: TrustBoxProps) => {
  return (
    <div
      ref={trustBoxRef}
      className="trustpilot-widget trustBox-micro-star"
      data-locale={locale}
      data-template-id="5419b732fbfb950b10de65e5"
      data-businessunit-id={TRUSTPILOT_BUSINESS_UNIT_ID}
      data-style-height="38px"
      data-font-family="Nunito Sans"
      data-qa="trustpilot-widget-micro-star"
      data-testid="trustpilot-widget-micro-star"
    >
      <a
        href={TRUSTPILOT_REVIEW_URL}
        target="_blank"
        rel="noopener"
      ></a>
    </div>
  );
};

/**
 * Trustpilot TrustBox widget.
 *
 * - Loads the Trustpilot bootstrap script itself with `lazyOnload` (after the page `load`
 *   event), so it never blocks rendering or hurts Core Web Vitals.
 * - Renders the widget in the given `locale` and re-renders it when the locale changes.
 * - Degrades gracefully: the wrapper reserves the widget height and stays invisible until the
 *   widget is initialized, so a blocked/failed script leaves no broken placeholder nor layout shift.
 */
export const Trustpilot = ({ variant = "mini", locale }: TrustpilotProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const widgetLocale = locale || TRUSTPILOT_DEFAULT_LOCALE;

  const loadTrustpilotWidget = useCallback(() => {
    if (!ref.current || typeof window.Trustpilot?.loadFromElement !== "function") {
      return;
    }

    try {
      window.Trustpilot.loadFromElement(ref.current, true);
      setIsInitialized(true);
    } catch {
      setIsInitialized(false);
    }
  }, []);

  // Re-render the widget whenever the locale changes (no-op until the script is ready).
  useEffect(() => {
    loadTrustpilotWidget();
  }, [loadTrustpilotWidget, widgetLocale]);

  const WidgetComponent = variant === "microStar" ? TrustpilotMicroStar : TrustpilotMini;

  return (
    <div
      style={{ visibility: isInitialized ? "visible" : "hidden" }}
      className={`trustpilot__wrapper trustpilot__wrapper--${variant}`}
    >
      <Script
        id="trustpilot-widget-bootstrap"
        src={TRUSTPILOT_SCRIPT_SRC}
        strategy="lazyOnload"
        onReady={loadTrustpilotWidget}
      />
      <WidgetComponent
        trustBoxRef={ref}
        locale={widgetLocale}
      />
    </div>
  );
};
